import gql from 'graphql-tag';
import {
  ActionPlanInputSchema,
  ContactSchema,
  FolderStructureItem,
  GuestSchema,
  RoomAclSchema,
  RoomGuestInputSchema,
  RoomJSONStructureItem,
  RoomJSONStructureSchema,
  RoomNotificationSettingsSchema,
  RoomObjectAclInputSchema,
  RoomSchema,
  SignablesJSONStructureSchema,
  UserSchema,
} from '../../definitions/schemas';
import { contactFields } from '../Contacts/ContactFields';
import { getPatchPayload } from '../../lib/GraphQL';
import { ListingConnection, ListingInput } from '../Listing';
import { PaginatedResourceInput } from '../Pagination';
import {
  roomEditFields,
  roomFields,
  roomListingFields,
  roomOverviewFields,
  verifyRoomContactAccessFields,
} from './RoomFields';
import { RoomAudience } from '../../enums/RoomAudience';
import { RoomLayout } from '../../enums/RoomLayout';
import { RoomStatus } from '../../enums/RoomStatus';
import { RoomType } from '../../enums/RoomTypes';
import { RoomCaller } from '../../enums/RoomCaller';
import Interface from '../Interface';

/**
 * Input for updating folder metadata during room creation/update
 * Maps to backend's FolderUpdateInputType
 */
export interface FolderUpdateInput {
  id: string;
  name?: string;
  structure?: {
    items: FolderStructureItem[];
  } | null;
  parentFolderId?: string | null;
}

interface GetRoomInput {
  uuid: string;
}

interface GetRoomData {
  getRoom: RoomSchema;
}

export interface SearchRoomsInput extends ListingInput {
  id?: string[];
  audience?: RoomAudience;
  collaboratorId?: string[];
  createdById?: string[];
  fromCreatedAt?: number | null;
  roomType: RoomType[];
  sharedWithMe?: boolean;
  status?: RoomStatus[];
  teamId?: string[];
  toCreatedAt?: number | null;
  userId?: string[];
  caller?: RoomCaller | null;
  rollingUptoMe?: boolean;
}

interface SearchRoomsData {
  rooms: ListingConnection<RoomSchema>;
}

export type RoomAclInput = PaginatedResourceInput<
  Pick<RoomAclSchema, 'id' | 'type'>
> & {
  copyGuests: boolean;
};

export type RoomGuestInput = PaginatedResourceInput<
  Pick<RoomGuestInputSchema, 'id' | 'type'>
>;

export interface RoomSettingsInput {
  acl?: RoomAclInput;
  expiresAt?: number;
  invitedContactsOnly: boolean;
  language?: string;
  layout: RoomLayout;
  notificationSettings?: RoomNotificationSettingsSchema;
  passcode?: string;
  primaryContact?: ContactSchema;
  requiresEmail: boolean;
  requiresExpirationDate: boolean;
  requiresPasscode: boolean;
  thumbnailAssetId?: string;
  thumbnailUri?: string;
  verifyEmail?: boolean;
}
export interface FolderChangeInput {
  id: string;
  name: string;
  description?: string;
  cover?: string;
  structure: RoomJSONStructureItem[];
}

export interface BaseRoomInput extends RoomSettingsInput {
  actionPlans?: ActionPlanInputSchema[];
  audience?: RoomAudience;
  bannerAssetId?: string;
  bannerUri?: string;
  buyerCopilotEnabled?: boolean;
  clientLogoUri?: string;
  clientLogoUrl?: string;
  companyId?: string;
  contactId?: string;
  dealId?: string;
  description?: string;
  enableContactCard?: boolean;
  enableQa?: boolean;
  folderChanges?: FolderChangeInput[];
  folders?: FolderUpdateInput[];
  guests?: RoomGuestInput;
  name: string;
  objectsAcl?: RoomObjectAclInputSchema;
  ownerId?: string;
  signablesStructure?: SignablesJSONStructureSchema;
  status?: RoomStatus;
  structure: RoomJSONStructureSchema;
}

interface CreateRoomInput extends BaseRoomInput {
  contentTemplateIds?: string[];
  roomTemplateId?: string;
  roomType: string;
}

interface CreateRoomData {
  createRoom: {
    room: RoomSchema;
  };
}

interface UpdateRoomInput extends Partial<Omit<BaseRoomInput, 'contacts'>> {
  id: string;
  // contacts is nullable for updates outside the room edit flow (e.g. change owner or status)
  contacts?: string[];
  contentTemplateIds?: string[];
  sellingTeam?: boolean;
  emailNotification?: boolean;
}

interface BulkUpdateRoomsInput {
  updateAction: string;
  updateFields: string[];
  newOwnerId: string;
  sellingTeam?: boolean;
  emailNotification?: boolean;
  selections: {
    operator: string;
    filter: {
      userId: string[];
      status: string[];
    };
  }[];
}

interface UpdateRoomData {
  updateRoom: {
    room: RoomSchema;
  };
}

interface BulkUpdateRoomsData {
  updateRooms: {
    rooms: RoomSchema[];
  };
}

interface DeleteRoomInput {
  id: string;
}

interface VerifyRoomAccessInput {
  consent?: boolean;
  email?: string;
  passcode?: string;
  roomContactToken?: string;
  uuid: string;
  verificationCode?: string;
}

export interface RoomAccessData {
  contact?: ContactSchema;
  pendingVerification: boolean;
  user?: UserSchema;
}

interface VerifyRoomAccessData {
  verifyRoomAccess: RoomAccessData;
}

interface ScheduleCallInput {
  roomId: string;
  message: string;
}

interface ScheduleCallData {
  scheduleCall: Record<string, unknown>;
}

interface RoomInviteeInput {
  email?: string;
  mobilePhone?: string;
}
interface ShareRoomInput {
  invitees: RoomInviteeInput[];
  roomId: string;
}

export interface ShareRoomData {
  shareRoom: {
    contacts?: ContactSchema[];
    link?: string;
  };
}

interface ReachOutContentInfluencerInput {
  contentId: string;
  roomId: string;
}

interface CreateRoomContactsContactInput {
  id?: string;
  firstName?: string | null;
  lastName?: string | null;
  mobilePhone?: string;
}

interface CreateRoomContactsInput {
  contacts: CreateRoomContactsContactInput[];
  roomId: string;
}

interface CreateRoomContactsData {
  createRoomContacts: {
    contacts: ContactSchema[];
  };
}

interface GuestGrantAccessInput {
  uuid: string;
  provideAccessToken: string;
}

interface GuestGrantAccesssData {
  guestGrantAccess: {
    guest: GuestSchema;
  };
}

export default class RoomsInterface extends Interface {
  public async getRoom(
    variables: GetRoomInput,
    withServerResponse = false,
    fields = roomFields
  ) {
    const response = await this.query<GetRoomData>(
      () =>
        this.apolloService.executeQuery<GetRoomData>({
          query: gql`
            query getRoom($uuid: String!) {
              getRoom(uuid: $uuid) {
                ${fields}
              }
            }
          `,
          variables,
        }),
      'Error loading room',
      withServerResponse
    );

    return response.getRoom;
  }

  public async getRoomWithRoomContacts(
    variables: GetRoomInput,
    fields: string
  ) {
    const response = await this.query<GetRoomData>(
      () =>
        this.apolloService.executeQuery<GetRoomData>({
          query: gql`
            query getRoom($uuid: String!) {
              getRoom(uuid: $uuid) {
                ${fields}
              }
            }
          `,
          variables,
        }),
      'Error loading room'
    );

    return response.getRoom;
  }

  public async getRoomOverview(
    variables: GetRoomInput,
    fields = roomOverviewFields
  ) {
    const response = await this.query<GetRoomData>(
      () =>
        this.apolloService.executeQuery<GetRoomData>({
          query: gql`
            query getRoom($uuid: String!) {
              getRoom(uuid: $uuid) {
                ${fields}
              }
            }
          `,
          variables,
        }),
      'Error loading room',
      true
    );

    return response.getRoom;
  }

  public async search(variables: SearchRoomsInput, fields = roomListingFields) {
    const response = await this.query<SearchRoomsData>(
      () =>
        this.apolloService.executeQuery<SearchRoomsData>({
          query: gql`
          query rooms(
            $audience: RoomAudienceEnum
            $collaboratorId: [ID!]
            $createdById: [ID!]
            $fromCreatedAt: Int
            $id: [ID!]
            $limit: Int
            $offset: Int
            $roomType: [RoomTypeEnum!]!
            $search: String
            $sharedWithMe: Boolean
            $sortBy: RoomSortEnum
            $sortDirection: SortEnum
            $status: [RoomStatusEnum!]
            $teamId: [ID!]
            $toCreatedAt: Int
            $userId: [ID!]
            $caller:[RoomCallerEnum!]
            $rollingUptoMe: Boolean
          ) {
            rooms(
              audience: $audience
              collaboratorId: $collaboratorId
              createdById: $createdById
              fromCreatedAt: $fromCreatedAt
              id: $id
              limit: $limit
              offset: $offset
              roomType: $roomType
              search: $search
              sharedWithMe: $sharedWithMe
              sortBy: $sortBy
              sortDirection: $sortDirection
              status: $status
              teamId: $teamId
              toCreatedAt: $toCreatedAt
              userId: $userId
              caller: $caller
              rollingUptoMe: $rollingUptoMe
            ) {
              totalCount
              nodes {
                ${fields}
              }
            }
          }
        `,
          variables,
        }),
      'Error loading rooms'
    );

    return response.rooms;
  }

  public async create(input: CreateRoomInput) {
    return this.mutate<CreateRoomData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createRoom($input: CreateRoomInput!) {
            createRoom(input: $input) {
              errors
              message
              room {
                ${roomEditFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.createRoom;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error creating room');
  }

  public async update(input: UpdateRoomInput) {
    return this.mutate<UpdateRoomData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateRoom($input: UpdateRoomInput!) {
            updateRoom(input: $input) {
              errors
              message
              room {
                ${roomEditFields}
              }
            }
          }
        `,
        variables: {
          input: getPatchPayload(input),
        },
      });

      const { errors, message } = response.data.updateRoom;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error updating room');
  }

  public async bulkUpdate(input: BulkUpdateRoomsInput) {
    return this.mutate<BulkUpdateRoomsData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateRooms($input: UpdateRoomsInput!) {
            updateRooms(input: $input) {
              errors
              message
              jobId
            }
          }
        `,
        variables: {
          input: getPatchPayload(input),
        },
      });

      const { errors, message } = response.data.updateRooms;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error updating rooms');
  }

  public async delete(input: DeleteRoomInput) {
    return this.mutate<null>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteRoom($input: DeleteRoomInput!) {
            deleteRoom(input: $input) {
              errors
              message
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.deleteRoom;

      return {
        data: null,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error deleting room');
  }

  public async verifyRoomAccess(
    input: VerifyRoomAccessInput,
    fields = verifyRoomContactAccessFields
  ) {
    return this.mutate<VerifyRoomAccessData>(
      async () => {
        const response = await this.apolloService.executeMutation({
          mutation: gql`
          mutation verifyRoomAccess($input: VerifyRoomAccessInput!) {
            verifyRoomAccess(input: $input) {
              errors
              ${fields}
            }
          }
        `,
          variables: {
            input,
          },
        });

        return {
          data: response.data,
          serverErrors: response.errors,
        };
      },
      'Error verifying room access',
      true
    );
  }

  public async scheduleCall(input: ScheduleCallInput) {
    return this.mutate<ScheduleCallData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation scheduleCall($input: ScheduleCallInput!) {
            scheduleCall(input: $input) {
              errors
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.scheduleCall;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error scheduling a call');
  }

  public shareRoom(input: ShareRoomInput) {
    return this.mutate<ShareRoomData>(
      async () => {
        const response = await this.apolloService.executeMutation({
          mutation: gql`
            mutation shareRoom($input: ShareRoomInput!) {
              shareRoom(input: $input) {
                errors
                message
                link
                contacts {
                  ${contactFields}
                }
              }
            }
          `,
          variables: {
            input,
          },
        });

        return {
          data: response.data,
          serverErrors: response.errors,
        };
      },
      'Error sharing room',
      true
    );
  }

  public reachOutContentInfluencer(input: ReachOutContentInfluencerInput) {
    return this.mutate<null>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation reachOutContentInfluencer(
            $input: ReachOutContentInfluencerInput!
          ) {
            reachOutContentInfluencer(input: $input) {
              errors
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.reachOutContentInfluencer;

      return {
        data: null,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error contacting customer');
  }

  public async createRoomContacts(
    input: CreateRoomContactsInput,
    fields = contactFields,
    rejectWithServerResponse = false
  ) {
    return this.mutate<CreateRoomContactsData>(
      async () => {
        const response = await this.apolloService.executeMutation({
          mutation: gql`
          mutation createRoomContacts($input: CreateRoomContactsInput!) {
            createRoomContacts(input: $input) {
              errors
              contacts {
                ${fields}
              }
            }
          }
        `,
          variables: {
            input,
          },
        });

        const { errors, message } = response.data.createRoomContacts;

        return {
          data: response.data,
          serverErrors: errors,
          serverMessage: message,
        };
      },
      'Error creating room contacts',
      rejectWithServerResponse
    );
  }

  public async guestGrantAccess(input: GuestGrantAccessInput) {
    return this.mutate<GuestGrantAccesssData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation guestGrantAccess($input: GuestGrantAccessInput!) {
            guestGrantAccess(input: $input) {
              errors
              guest {
                id
                type
                email
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      const { errors, message } = response.data.guestGrantAccess;

      return {
        data: response.data,
        serverErrors: errors,
        serverMessage: message,
      };
    }, 'Error providing access to guest');
  }
}
